import React from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap,
  CheckCircle,
  Clock,
  Users,
  FileText,
  DollarSign,
  BarChart3,
  Zap,
  Shield,
  ArrowRight,
  BookOpen,
  School,
  Users2,
  Calendar,
  MapPin,
  Building
} from 'lucide-react';

const EducationIndustryPage: React.FC = () => {
  return (
    <div className="bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-green-50 to-green-100 dark:from-gray-800 dark:to-gray-900">
        <div className="absolute inset-0 bg-gradient-to-r from-green-600/10 to-green-800/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-4">
                <GraduationCap className="h-8 w-8 text-green-600 mr-3" />
                <span className="text-sm font-semibold text-green-600 uppercase tracking-wider">Education</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                Streamline Your Educational Institution Operations
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
                Manage facility maintenance, equipment repairs, and campus operations for schools, universities, and training centers with WorkOrder HQ™. 
                From classroom maintenance to campus-wide projects, keep your educational facility running smoothly.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/signup?industry=2"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all transform hover:scale-105 shadow-lg"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-green-600 bg-white dark:bg-gray-800 border-2 border-green-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                >
                  View Pricing
                </Link>
              </div>
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                14-day free trial • No credit card required • Cancel anytime
              </p>
            </div>
            <div className="relative">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8">
                <div className="space-y-6">
                  <div className="flex items-center">
                    <School className="h-8 w-8 text-green-600 mr-4" />
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">Campus Maintenance</p>
                      <p className="text-gray-600 dark:text-gray-400">Classrooms, labs & facilities</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <BookOpen className="h-8 w-8 text-green-500 mr-4" />
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">Technology Support</p>
                      <p className="text-gray-600 dark:text-gray-400">IT equipment & AV systems</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Users2 className="h-8 w-8 text-green-700 mr-4" />
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">Student Services</p>
                      <p className="text-gray-600 dark:text-gray-400">Dormitories & dining facilities</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Education-Specific Features */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Built for Educational Institutions
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Everything you need to manage your educational facility efficiently
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-green-100 dark:bg-green-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Calendar className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Academic Calendar Integration
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Schedule maintenance around academic calendars, breaks, and special events to minimize disruptions.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-blue-100 dark:bg-blue-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <BookOpen className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Technology Management
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Track and maintain classroom technology, computer labs, and AV equipment across campus.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-purple-100 dark:bg-purple-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Building className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Multi-Building Management
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Manage maintenance across multiple buildings, departments, and campus locations efficiently.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-orange-100 dark:bg-orange-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Emergency Response
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Handle emergency maintenance requests with priority dispatch for safety and security issues.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-red-100 dark:bg-red-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Safety Compliance
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Maintain safety inspections, fire systems, and emergency equipment to protect students and staff.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-indigo-100 dark:bg-indigo-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <BarChart3 className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Budget Management
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Track maintenance costs, budget allocations, and project expenses across departments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Common Education Use Cases
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              See how educational institutions use WorkOrder HQ™ to streamline operations
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Use Case 1 */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
              <div className="flex items-center mb-4">
                <School className="h-8 w-8 text-green-600 mr-3" />
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">K-12 Schools</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Manage classroom maintenance, playground equipment, and facility operations for elementary and high schools.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Classroom furniture & equipment maintenance
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Playground & sports facility maintenance
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  HVAC & lighting system management
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Safety & security system maintenance
                </li>
              </ul>
            </div>

            {/* Use Case 2 */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
              <div className="flex items-center mb-4">
                <GraduationCap className="h-8 w-8 text-green-600 mr-3" />
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Universities & Colleges</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Handle complex campus operations including dormitories, research facilities, and large-scale infrastructure projects.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Student housing maintenance
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Research lab equipment maintenance
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Campus infrastructure projects
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Dining & recreational facility maintenance
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Why Educational Institutions Choose WorkOrder HQ™
              </h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Minimize Learning Disruptions
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Schedule maintenance during breaks and off-hours to keep students learning.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Ensure Student Safety
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Maintain safe learning environments with proper facility maintenance and safety systems.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Optimize Budget Usage
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Track maintenance costs and optimize spending across departments and facilities.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Improve Campus Operations
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Streamline maintenance workflows and improve operational efficiency across campus.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-lg p-8 text-white">
              <div className="space-y-6">
                <div className="flex items-center">
                  <Clock className="h-8 w-8 mr-4" />
                  <div>
                    <p className="text-3xl font-bold">14 Days</p>
                    <p className="text-green-100">Free Trial</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Users className="h-8 w-8 mr-4" />
                  <div>
                    <p className="text-3xl font-bold">Up to 10</p>
                    <p className="text-green-100">Technicians</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <FileText className="h-8 w-8 mr-4" />
                  <div>
                    <p className="text-3xl font-bold">Unlimited</p>
                    <p className="text-green-100">Work Orders</p>
                  </div>
                </div>
                <Link
                  to="/signup?industry=2"
                  className="block w-full bg-white text-green-600 text-center py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors mt-6"
                >
                  Get Started Free
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-green-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Educational Institution Operations?
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Join educational institutions who trust WorkOrder HQ™ to streamline their operations and maintain safe learning environments.
          </p>
          <Link
            to="/signup?industry=2"
            className="inline-flex items-center px-8 py-4 text-lg font-semibold text-green-600 bg-white rounded-lg hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg"
          >
            Start Your Free Trial
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
          <p className="mt-4 text-sm text-green-100">
            No credit card required • Full access to all features
          </p>
        </div>
      </section>
    </div>
  );
};

export default EducationIndustryPage;
